<template>
  <div class="mt-1">
    <div class="mb-2">
      <h6>Polizze Vive</h6>
      <div class="d-flex justify-content-end mt-2 mb-3" style="gap: 10px">
        <b-button
          class="btn btn--create btn--smaller"
          type="button"
          variant="primary"
          size="sm"
          :title="`Associa una nuova polizza all'anagrafica${
            currentRegistry ? ` di ${currentRegistry}` : ''
          }`"
          @click="onAdd"
        >
          <base-icon name="circled_plus" width="22" height="22" />
          Aggiungi polizza
        </b-button>
      </div>
      <export-options
        :exportUrl="exportUrl['filterInsurancePolicyDetail1']"
        :repository="repository"
        :resource="resource"
        :tableRef="$refs[tableRef['filterInsurancePolicyDetail1']]"
        :filter="filter"
        :options="[
          {
            code: 'EXPOINPO',
            label: null,
            formats: ['csv', 'pdf'],
          },
        ]"
      ></export-options>
      <table-policies
        :isTableInModal="true"
        infomodalName="P1"
        :fields="fields"
        repository="insurance_policy"
        resource="insurance_policies"
        :filterName="filterName1"
        :parentResourceId="parentResourceId"
        ref="InsurancePolicyDetailTableRef1"
        :filterOn="{
          byRelations: ['byDocument', 'byBroker', 'byTask'],
        }"
        @open_note="openNoteModal"
        @open_document="openDocumentModal"
        :onlyActions="['infomodal']"
        noInnerWidth
        noInputPaginator
        sortField="covered_at"
        includeBottomRow
        noPagination
      >
      </table-policies>
    </div>
    <div class="closed-insurance-policies">
      <h6>Polizze Estinte</h6>
      <export-options
        :exportUrl="exportUrl['filterInsurancePolicyDetail2']"
        :repository="repository"
        :resource="resource"
        :tableRef="$refs[tableRef['filterInsurancePolicyDetail2']]"
        :filter="filter"
        :options="[
          {
            code: 'EXPOINPO',
            label: null,
            formats: ['csv', 'pdf'],
          },
        ]"
      ></export-options>
      <table-policies
        :isTableInModal="true"
        infomodalName="P2"
        :fields="fields"
        repository="insurance_policy"
        resource="insurance_policies"
        :filterName="filterName2"
        :parentResourceId="parentResourceId"
        ref="InsurancePolicyDetailTableRef2"
        :filterOn="{
          byRelations: ['byDocument', 'byBroker', 'byTask'],
        }"
        @open_note="openNoteModal"
        @open_document="openDocumentModal"
        :onlyActions="['infomodal']"
        noInnerWidth
        noInputPaginator
        sortField="expired_at"
        :descending="true"
        includeBottomRow
        noPagination
      >
      </table-policies>
    </div>
    <note-detail-modal :items="notes" id="noteDetailModalPolicy">
    </note-detail-modal>
    <document-detail-modal :items="documents" id="documentDetailModalPolicy">
    </document-detail-modal>
  </div>
</template>

<script>
import TablePolicies from "@/components/tables/Policies";
import ExportOptions from "@/components/ExportOptions";
import NoteDetailModal from "@/components/modals/noteDetailModal";
import DocumentDetailModal from "@/components/modals/documentDetailModal";
import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import CustomFiltersMixin from "@/mixins/CustomFiltersMixin";
import BaseIcon from "@/components/BaseIcon";
import { toLocaleCurrency } from "@/utils/strings";
import { toLocaleDate } from "@/utils/dates";
import { mapGetters, mapActions } from "vuex";
import { getDictionary } from "@/utils/dictionary";

export default {
  name: "InsurancePolicy",
  mixins: [CustomFiltersMixin],
  data() {
    return {
      onLandData: {},
      exportUrl: {
        filterInsurancePolicyDetail1: null,
        filterInsurancePolicyDetail2: "InsurancePolicyDetailTableRef2",
      },
      notes: [],
      documents: [],
      filter: {
        filterInsurancePolicyDetail1: this.initFilter1(),
        filterInsurancePolicyDetail2: this.initFilter2(),
      },
      filterName1: "filterInsurancePolicyDetail1",
      filterName2: "filterInsurancePolicyDetail2",
      repository: "insurance_policy",
      resource: "insurance_policies",
      parentResourceId: this.resourceId,
      tableRef: {
        filterInsurancePolicyDetail1: "InsurancePolicyDetailTableRef1",
        filterInsurancePolicyDetail2: "InsurancePolicyDetailTableRef2",
      },
      fields: [
        {
          key: "insurer.title",
          label: this.getDictionary("title", "insurer"),
          sortable: true,
          sortKey: "byInsurer.title",
        },
        {
          key: "number",
          label: this.getDictionary("number", "insurance_policy"),
          sortable: true,
          sortKey: "byAttribute.number",
        },
        {
          key: "contraente",
          label: this.getDictionary("contractor"),
          sortable: true,
          sortKey: "byRegistry.title",
        },
        {
          // Ramo
          key: "insurance_risk.risk_branch.title",
          value: "insurance_risk.risk_branch.title",
          label: this.getDictionary("risk_branch_id", "insurance_risk"),
          sortable: true,
          sortKey: "byRiskBranch.code",
        },
        {
          // Prodotto
          key: "insurance_risk",
          value: "insurance_risk.title",
          label: this.getDictionary("insurance_risk"),
        },
        {
          key: "effective_at",
          label: this.getDictionary("effective_at", "insurance_policy"),
          sortable: true,
          formatter: (value) => toLocaleDate(value),
          sortKey: "effective_at",
        },
        {
          key: "covered_at",
          label: this.getDictionary("covered_at", "insurance_policy"),
          sortable: true,
          formatter: (value) => toLocaleDate(value),
          sortKey: "covered_at",
        },
        {
          key: "expired_at",
          label: this.getDictionary("expired_at", "insurance_policy"),
          sortable: true,
          formatter: (value) => toLocaleDate(value),
          sortKey: "expired_at",
        },
        {
          key: "payment_splitting.text",
          label: this.getDictionary("payment_splitting", "insurance_policy"),
        },
        {
          key: "gross_premium",
          value: "gross_premium",
          label: this.getDictionary("gross_premium", "insurance_policy"),
          formatter: (value) => toLocaleCurrency(value),
          sortable: true,
          thClass: "text-right",
          thStyle: {
            "text-align": "right",
          },
          tdClass: "text-right",
        },
        {
          key: "gross_payment_splitting",
          value: (item) =>
            toLocaleCurrency(
              item.gross_premium *
                (12 / (parseInt(item.payment_splitting.value) || 12))
            ),
          label: this.getDictionary(
            "gross_payment_splitting",
            "insurance_policy"
          ),
          sortable: true,
          sortKey: "byAttribute.gross_premium",
          formatter: (value) => toLocaleCurrency(value),
          thClass: "text-right",
          thStyle: {
            "text-align": "right",
          },
          tdClass: "text-right",
        },
        {
          key: "status_policy.text",
          label: this.getDictionary("status_policy", "insurance_policy"),
          sortable: true,
          sortKey: "byAttribute.status_policy",
        },
        {
          key: "broker",
          label: this.getDictionary("salesman"),
          sortable: true,
          sortKey: "byBroker.BUID",
        },
        {
          key: "mandate_code_id",
          label: this.getDictionary("mandate_code", "insurance_policy"),
          formatter: (value) =>
            this.getMandateCodes()().find((m) => m.value === value)?.code ??
            value,
          sortable: true,
          sortKey: "byAttribute.mandate_code_id",
        },
        {
          key: "note_counter",
          label: this.getDictionary("note"),
        },
        {
          key: "document_counter",
          label: this.getDictionary("document"),
        },
      ],
    };
  },
  components: {
    BaseIcon,
    TablePolicies,
    NoteDetailModal,
    DocumentDetailModal,
    ExportOptions,
  },
  props: {
    resourceId: Number,
    dbRowData: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  methods: {
    getDictionary,
    initFilter1() {
      let init = {
        byRegistry: { id: this.resourceId },
        byAttribute: { status_policy: [0, 1, 2] },
      };
      return init;
    },
    initFilter2() {
      let init = {
        byRegistry: { id: this.resourceId },
        byAttribute: { status_policy: [3, 4, 5, 6, 7, 8, 9, 10, 11] },
      };
      return init;
    },
    onAdd() {
      if (document.querySelector("#searchResults"))
        this.$bvModal.hide("searchResults");
      if (document.querySelector("#searchResultDetails"))
        this.$bvModal.hide("searchResultDetails");
      this.$router.push({
        name: `${this.resource}.create`,
        params: {
          onLandData: this.onLandData,
        },
      });
    },
    openNoteModal(array) {
      this.notes = array;
      this.$bvModal.show("noteDetailModalPolicy");
    },
    openDocumentModal(array) {
      this.documents = array;
      this.$bvModal.show("documentDetailModalPolicy");
    },
    onSearch(name) {
      let criteria = this.filter[name];
      this.saveFilterByName({ name, criteria });
      this.exportUrl[name] = this.$refs[this.tableRef[name]].loadFilter(
        this.$refs[this.tableRef[name]].filterName
      );
      this.$refs[this.tableRef[name]]
        .fetch()
        .then(() => {
          this.removeFilterByName(name);
        })
        .catch((error) => {
          let errMsg = this.$getErrorMessage(error);
          this.$showSnackbar({ preset: "error", text: errMsg });
        });
    },
    _fetchCorrespondencesIfNeeded() {
      if (this.onLandData?.correspondences?.length === 0) {
        const Repo = RepositoryFactory.get("correspondence");
        const queryString = `page=1&perPage=0&byRegistry[id]=${this.resourceId}`;
        Repo.index(queryString).then((response) => {
          const correspondence_obj = response.data.data ?? [];
          if (correspondence_obj.length > 0) {
            this.onLandData.correspondences = correspondence_obj;
          }
        });
      }
    },
    ...mapActions("filters", {
      saveFilterByName: "saveByName",
      removeFilterByName: "removeByName",
    }),
    ...mapGetters("filters", { loadFilterByName: "loadByName" }),
    ...mapGetters("auth", {
      getMandateCodes: "mandate_codes",
    }),
  },
  mounted() {
    if (Object.keys(this.dbRowData).length === 0 && this.resourceId) {
      const Repo = RepositoryFactory.get(this.repository);
      const queryString = `page=1&perPage=1&byRegistryGroup&byInsurancePolicy&byBroker&byTask&byDocument&byPrivacy&byRegistry[id]=${this.resourceId}`;
      Repo.index(queryString).then((response) => {
        const registry_obj = response.data.data;
        if (registry_obj && Object.keys(this.dbRowData).length > 0) {
          this.onLandData = registry_obj;
        }
        this._fetchCorrespondencesIfNeeded();
      });
    } else {
      this.onLandData = this.dbRowData;
      this._fetchCorrespondencesIfNeeded();
    }
    // this.removeFilterByName(this.filterName1);
    // this.removeFilterByName(this.filterName2);
    this.onSearch(this.filterName1);
    this.onSearch(this.filterName2);
  },
  computed: {
    currentRegistry() {
      if (!this.dbRowData?.title) return "";
      return this.dbRowData?.title;
    },
  },
};
</script>

<style scoped>
.closed-insurance-policies {
  margin-top: 30px;
}
</style>
